"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { navigationTargetFromClick } from "@/lib/route-progress";

/**
 * A 3 px brand-coloured bar along the top edge while a navigation is in
 * flight.
 *
 * Measured 2026-09-03: a click on "Road to FAANG" showed nothing for 4.8 s and
 * people clicked again. The wait is 0.5 s now that the public pages are cached,
 * but a course page still renders on demand and a slow connection stretches
 * everything, and the App Router has no navigation event to hook. So: a
 * capture-phase click listener starts the bar for any internal link
 * (`lib/route-progress.ts` decides which clicks count), the bar creeps toward
 * 85 % while waiting, and the pathname or query changing completes it. A
 * navigation that never commits (a link whose handler opens a dialog instead)
 * is closed out by a stall timer.
 *
 * Presence is the signal: under `prefers-reduced-motion` the width and fade
 * transitions are off and the bar simply appears and disappears (`docs/03`
 * §12). Programmatic `router.push` calls are not covered; the router commits
 * `pushState` only after the data arrives, so there is nothing early to listen
 * for.
 */

const SETTLE_MS = 250;
const TRICKLE_MS = 300;
const STALL_MS = 8_000;
const START = 0.12;
const CEILING = 0.85;

type Timers = {
  trickle?: ReturnType<typeof setInterval>;
  settle?: ReturnType<typeof setTimeout>;
  stall?: ReturnType<typeof setTimeout>;
};

export function RouteProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const routeKey = `${pathname}?${searchParams.toString()}`;

  const [active, setActive] = useState(false);
  const [value, setValue] = useState(0);
  const activeRef = useRef(false);
  const timers = useRef<Timers>({});

  const clearTimers = () => {
    const t = timers.current;
    if (t.trickle) clearInterval(t.trickle);
    if (t.settle) clearTimeout(t.settle);
    if (t.stall) clearTimeout(t.stall);
    timers.current = {};
  };

  const finish = () => {
    if (!activeRef.current) return;
    clearTimers();
    setValue(1);
    timers.current.settle = setTimeout(() => {
      activeRef.current = false;
      setActive(false);
      setValue(0);
    }, SETTLE_MS);
  };

  const start = () => {
    clearTimers();
    activeRef.current = true;
    setActive(true);
    setValue(START);
    timers.current.trickle = setInterval(() => {
      setValue((v) => v + (CEILING - v) * 0.12);
    }, TRICKLE_MS);
    timers.current.stall = setTimeout(finish, STALL_MS);
  };

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (!navigationTargetFromClick(event, anchor, window.location)) return;
      start();
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
    // start/finish read refs and setters only; the listener is registered once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The route committed: run the bar to the end and let it settle.
  useEffect(() => {
    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  useEffect(() => clearTimers, []);

  return (
    <div
      aria-hidden="true"
      data-route-progress={active ? "active" : "idle"}
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[3px]"
    >
      <div
        className="h-full bg-[rgb(var(--primary))] transition-[width,opacity] duration-200 ease-out motion-reduce:transition-none"
        style={{ width: `${Math.round(value * 100)}%`, opacity: active ? 1 : 0 }}
      />
    </div>
  );
}
